import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';

const T1_DEDICATED = ['F1','F2','F3','F4','F5','F6','F7','F8','F9','F10'];

export default function AlertPage() {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const { workshopStates, updateWorkshopState } = useSocket();
  const { operator } = useAuth();
  const [toast, setToast] = useState(null);
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);

  const state = workshopStates[workshopId] || {};
  const isActiveByMe = state.status === 'busy' && state.lockedBy === operator;
  const isLockedByOther = state.status === 'busy' && state.lockedBy && state.lockedBy !== operator;
  const isDedicatedBlock = T1_DEDICATED.includes(workshopId) && operator === 'T2';

  useEffect(() => {
    if (isDedicatedBlock) { navigate('/'); return; }
    fetch(`${API_BASE_URL}/api/logs?workshop=${workshopId}&limit=6`)
      .then(r => r.json()).then(setLogs).catch(() => {});
  }, [workshopId, isDedicatedBlock, navigate]);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleAlert(action) {
    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/workshops/alert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workshop: workshopId, action, operator })
      });
      const data = await res.json();
      if (data.success) {
        showToast(`${workshopId} ${action} sent · payload: ${data.payload}`, 'success');
        updateWorkshopState(workshopId, action, operator);
        fetch(`${API_BASE_URL}/api/logs?workshop=${workshopId}&limit=6`).then(r => r.json()).then(setLogs).catch(() => {});
      } else {
        showToast(data.error || 'Failed to send alert', 'error');
      }
    } catch {
      showToast('Server not reachable', 'error');
    }
    setSending(false);
  }

  function fmtTime(t) {
    return new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  const isShared = !T1_DEDICATED.includes(workshopId);
  const statusBadge = isActiveByMe ? 'badge badge-active'
    : isLockedByOther ? 'badge badge-locked'
    : 'badge badge-available';
  const statusText = isActiveByMe ? 'Active'
    : isLockedByOther ? `Locked by ${state.lockedBy}`
    : 'Available';

  return (
    <div>
      <button className="back-btn" onClick={() => navigate('/')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        All Workshops
      </button>

      {isLockedByOther && (
        <div className="locked-banner">
          <div className="locked-banner-title">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            {workshopId} is locked
          </div>
          <div className="locked-banner-body">
            {state.lockedBy} is currently using {workshopId}. It will unlock once they send {workshopId} OFF.
          </div>
        </div>
      )}

      <div className="detail-card">
        <div className="detail-row">
          <span className="detail-key">Workshop</span>
          <span className="detail-val">{workshopId}</span>
        </div>
        <div className="detail-row">
          <span className="detail-key">Type</span>
          <span className="detail-val" style={{ color: isShared ? '#6a0dad' : '#003580', fontWeight: 600 }}>
            {isShared ? 'Shared — T1 & T2' : 'T1 Dedicated'}
          </span>
        </div>
        <div className="detail-row">
          <span className="detail-key">Operator</span>
          <span className="detail-val">{operator === 'T1' ? 'Traverser 1' : 'Traverser 2'}</span>
        </div>
        <div className="detail-row">
          <span className="detail-key">Status</span>
          <span className={statusBadge}>{statusText}</span>
        </div>
      </div>

      <p className="section-label">Send alert for {workshopId}</p>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            {toast.type === 'success'
              ? <polyline points="20 6 9 17 4 12"/>
              : <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>}
          </svg>
          {toast.msg}
        </div>
      )}

      <div className="alert-btns">
        <button
          className="btn-on"
          onClick={() => handleAlert('ON')}
          disabled={sending || isLockedByOther}
        >
          <span className="btn-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              <line x1="12" y1="2" x2="12" y2="4"/>
            </svg>
          </span>
          <div className="btn-label">{workshopId} ON</div>
          <div className="btn-sub">Start alert</div>
        </button>

        <button
          className="btn-off"
          onClick={() => handleAlert('OFF')}
          disabled={sending || isLockedByOther}
        >
          <span className="btn-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              <path d="M18.63 13A17.89 17.89 0 0 1 18 8"/>
              <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"/>
              <path d="M18 8a6 6 0 0 0-9.33-5"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          </span>
          <div className="btn-label">{workshopId} OFF</div>
          <div className="btn-sub">End &amp; unlock</div>
        </button>
      </div>

      <p className="section-label" style={{ marginTop: 4 }}>Recent activity</p>
      <div className="log-box">
        {logs.length === 0
          ? <div style={{ padding: 12, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No activity yet</div>
          : logs.map((l, i) => (
            <div className="log-row" key={i}>
              <span>
                <span className={l.action === 'ON' ? 'log-on' : 'log-off'}>{l.workshop} {l.action}</span>
                {l.operator && <span className="log-op">· {l.operator}</span>}
              </span>
              <span className="log-time">{fmtTime(l.time)}</span>
            </div>
          ))}
      </div>
    </div>
  );
}
